// Pure reconciliation helpers for the RoomCaptureScreen subscriber.
// Extracted from room-capture-screen.tsx so that file stays under the
// max-lines policy. No DOM, no React — these are easy to unit-test
// against the SpaceWithItems shape.

import type {
  CaptureItemRow,
  CaptureMutationResult,
  SpaceWithItems,
} from '@citycare/shared'

import type { AssetRowPatch } from '../components/asset-row'

export interface SystemBucket {
  systemId: string
  systemName: string
  items: CaptureItemRow[]
}

// Group items by system, preserving insertion order within each
// bucket and sorting buckets by systemName.
export function groupBySystem(items: CaptureItemRow[]): SystemBucket[] {
  const buckets = new Map<string, SystemBucket>()
  for (const item of items) {
    const bucket = buckets.get(item.systemId)
    if (bucket === undefined) {
      buckets.set(item.systemId, {
        systemId: item.systemId,
        systemName: item.systemName,
        items: [item],
      })
    } else {
      bucket.items.push(item)
    }
  }
  return Array.from(buckets.values()).sort((a, b) =>
    a.systemName.localeCompare(b.systemName),
  )
}

export function applyOptimisticPatch(
  item: CaptureItemRow,
  patch: AssetRowPatch,
): CaptureItemRow {
  return {
    ...item,
    quantity: patch.quantity ?? item.quantity,
    isZeroConfirmed: patch.isZeroConfirmed ?? item.isZeroConfirmed,
  }
}

export function isCaptureItemRow(
  candidate: unknown,
): candidate is CaptureItemRow {
  if (candidate === null || typeof candidate !== 'object') return false
  const row = candidate as Partial<CaptureItemRow>
  return typeof row.id === 'string' && typeof row.quantity === 'string'
}

// In-flight createItem tracker entry. We hold the user's LATEST
// optimistic values for a synthetic row so that when the server's
// authoritative row arrives (potentially echoing the FIRST tap's
// quantity due to first-write-wins), the reconciler can detect that
// the user has typed past the server and queue a follow-up
// updateItem.
export interface PendingCreate {
  quantity: string
  isZeroConfirmed: boolean
  notes: string | null
  createResult?: Promise<CaptureMutationResult>
}

// Returns the authoritative row when the server is in sync, OR a
// merged row (authoritative shell + local optimistic values) when the
// user tapped past the server while createItem was in flight.
//
// `pending` is consumed: callers should delete it from the map before
// or after invoking this; the function itself is pure.
export function mergeAuthoritativeWithPending(
  authoritative: CaptureItemRow,
  pending: PendingCreate | undefined,
): { row: CaptureItemRow; serverIsBehind: boolean } {
  if (pending === undefined) {
    return { row: authoritative, serverIsBehind: false }
  }
  if (
    pending.quantity === authoritative.quantity &&
    pending.isZeroConfirmed === authoritative.isZeroConfirmed
  ) {
    return { row: authoritative, serverIsBehind: false }
  }
  return {
    row: {
      ...authoritative,
      quantity: pending.quantity,
      isZeroConfirmed: pending.isZeroConfirmed,
      notes: pending.notes,
    },
    serverIsBehind: true,
  }
}

// Carry the JOIN-derived reference/display fields of an existing row
// forward when the echoed authoritative row arrives without them.
//
// The PowerSync echo rebuilds a row by JOINing the synced reference
// tables (asset_types, asset_sub_systems, asset_systems). Right after a
// WiFi drop those tables can lag replication, so the JOIN returns NULLs.
// Two echo paths surface that differently: the updateItem echo
// (capture-client-powersync.ts) fills empty strings + a 'Count' UoM
// default; the createItem echo (capture-client-powersync-create-item.ts)
// passes the raw `null` JOIN values straight through. If that echo
// blindly replaced a populated optimistic row, applying a quantity would
// leave the row nameless and reorder it into a blank-titled System group
// (groupBySystem buckets on systemId), and — worse — flip a measurement
// asset (m/m²/m³) to the integer Count keypad mid-capture. That is the
// "components go blank after applying a quantity value" field report.
//
// The authoritative quantity / syncVersion / id / flags ALWAYS win.
// Name fields fall back per-field via `||` (a genuine rename still
// propagates because the echo carries the new, non-empty name; `null`
// and `''` are both falsy so a lagging JOIN falls back). unit_of_measure
// / grouping_convention CANNOT use an emptiness fallback — 'Count' is a
// valid, truthy default — so they are gated on `joinResolved`. That gate
// uses a TRUTHY check (not `!== ''`) so a raw `null` from the createItem
// echo counts as unresolved; otherwise `null !== ''` would read as
// resolved and overwrite the UoM with null.
//
// Exported for reuse by the site-capture echo path (site-capture-screen),
// which has the same lagging-JOIN clobber.
export function preserveDisplayFields(
  prev: CaptureItemRow,
  next: CaptureItemRow,
): CaptureItemRow {
  const joinResolved = Boolean(next.assetTypeName) || Boolean(next.systemId)
  return {
    ...next,
    assetTypeName: next.assetTypeName || prev.assetTypeName,
    systemId: next.systemId || prev.systemId,
    systemName: next.systemName || prev.systemName,
    subSystemId: next.subSystemId || prev.subSystemId,
    subSystemName: next.subSystemName || prev.subSystemName,
    unitOfMeasure: joinResolved ? next.unitOfMeasure : prev.unitOfMeasure,
    groupingConvention: joinResolved
      ? next.groupingConvention
      : prev.groupingConvention,
  }
}

// Replace a row in `items` matching `next` by id (persisted update),
// falling back to assetTypeId when the existing row is the synthetic
// id-empty placeholder — OR when the existing row is persisted under a
// DIFFERENT id (see below). Returns the next items array, or appends
// only when the asset type is entirely new to the room (e.g. Add Asset
// dialog row that wasn't in the template). On replace, reference/display
// fields the echo dropped are preserved from the existing row (see
// preserveDisplayFields).
//
// Why the same-assetTypeId-different-id branch REPLACES (field incident
// 2026-07-13, Teghan / Linton M02 — "increasing the light fitting will
// increase all the other listings"): an echo can legitimately carry a NEW
// capture_items id for an asset type already persisted on screen —
// session recovery re-derives the deterministic id under the recovered
// session (capture-item-id.ts), the rejected-tombstone fallback walk
// mints a new generation, and the re-home path adopts the server's
// authoritative id. Appending in that case leaves TWO rows sharing one
// assetTypeId; SystemGroup keys rows by assetTypeId and handleUpdate
// routes patches by assetTypeId, so a single + tap then visibly moves
// every copy — the "adding it to everything" fan-out. Within one session
// a space can hold at most one live row per asset type (the server's
// partial unique index capture_items_session_space_asset_unique), so
// assetTypeId is a legal row identity here and the fresher echo's id wins
// so later PATCHes target the authoritative server row.
//
// Staleness guard (audit-1 #1): the same-assetTypeId-different-id replace is
// gated on syncVersion so a STALE (lower-syncVersion) echo re-homing a
// PERSISTED row cannot regress the on-screen count nor adopt an
// older-generation id (which may already be a tombstone → a PATCH against it
// would re-enter the not-found churn). This branch is unreachable today — the
// reconcile bus is fed ONLY by this device's own createItem/updateItem
// dispatch-result readbacks (capture-client-events.ts), which are per-id
// monotonic; the connector re-home/recovery paths emit to a SEPARATE
// telemetry bus (_emitUploaded), never here. The guard makes "the
// authoritative syncVersion wins" true by construction rather than by that
// topology assumption. The synthetic-placeholder (id '') and same-id branches
// are exempt: a synthetic row has no count to protect, and a same-id echo is
// the same row updating.
export function upsertItemByIdOrAssetType(
  items: readonly CaptureItemRow[],
  next: CaptureItemRow,
): CaptureItemRow[] {
  let idx = items.findIndex((it) => it.id !== '' && it.id === next.id)
  if (idx < 0) {
    idx = items.findIndex((it) => it.assetTypeId === next.assetTypeId)
  }
  if (idx >= 0) {
    const out = items.slice()
    const prev = out[idx]
    if (
      prev !== undefined &&
      prev.id !== '' &&
      prev.id !== next.id &&
      next.syncVersion < prev.syncVersion
    ) {
      // Stale re-home echo for a persisted row: the on-screen row is fresher.
      // Keep it wholesale (last-write-wins by syncVersion) — do not regress
      // the count and do not adopt the stale echo's id.
      out[idx] = prev
    } else {
      // prev is defined at runtime (idx came from findIndex); the
      // `=== undefined ? next` only satisfies noUncheckedIndexedAccess.
      out[idx] = prev === undefined ? next : preserveDisplayFields(prev, next)
    }
    return out
  }
  return [...items, next]
}

// Apply `next` to the matching space inside a SessionDetail-shaped
// spaces array. Pure — non-matching spaces are returned by reference.
export function upsertItemInSpaces(
  spaces: readonly SpaceWithItems[],
  spaceId: string,
  next: CaptureItemRow,
): SpaceWithItems[] {
  return spaces.map((s) =>
    s.id === spaceId
      ? { ...s, items: upsertItemByIdOrAssetType(s.items, next) }
      : s,
  )
}
